import type {
    LeaveRequest,
    LeaveRequestAttachment,
} from "@spork/s4p-server-sdk";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import { toastError, toastSuccess } from "@/components/AppToast";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/text";
import { canParentManageLeaveRequestAttachments } from "@/features/home/leaveRequestStatus";
import { showAlert } from "@/lib/showAlert";
import { log } from "@/logger";
import { TEST_IDS } from "@/testIdDefinition";
import {
    useCreateLeaveRequestAttachmentMutation,
    useRemoveLeaveRequestAttachmentMutation,
} from "@/query/apiSlice";
import { query } from "@/query/queries";

import { AttachmentPicker } from "./AttachmentPicker";
import { AttachmentFileList } from "./AttachmentFileList";
import {
    MAX_ATTACHMENTS,
    MAX_ATTACHMENT_BYTES,
    readAttachmentAsBase64,
    resolveAttachmentMimeType,
    type AttachmentFile,
} from "./attachmentUtils";

type LeaveRequestAttachmentsPanelProps = {
    request: LeaveRequest;
    onAdded?: (id: number) => void;
    onRemoved?: (id: number) => void;
    onBusyChange?: (busy: boolean) => void;
    onPickerSessionChange?: (active: boolean) => void;
};

function getErrorMessage(error: unknown, fallback: string): string {
    if (error && typeof error === "object" && "message" in error) {
        const message = (error as { message?: unknown }).message;
        if (typeof message === "string" && message.trim()) {
            return message;
        }
    }

    return fallback;
}

function toListFile(attachment: LeaveRequestAttachment): AttachmentFile {
    return {
        id: String(attachment.id),
        name: attachment.original_filename,
        size: attachment.size_bytes,
        mimeType: attachment.mime_type,
        uri: "",
    };
}

/**
 * Manages leave-request attachments. Uploads always skip the per-file staff
 * email; the parent screen batches that via notifyAdded / resubmit.
 */
export function LeaveRequestAttachmentsPanel({
    request,
    onAdded,
    onRemoved,
    onBusyChange,
    onPickerSessionChange,
}: LeaveRequestAttachmentsPanelProps) {
    const { t } = useTranslation();
    const [busyId, setBusyId] = useState<string | null>(null);
    const [createAttachment, { isLoading: isCreating }] =
        useCreateLeaveRequestAttachmentMutation();
    const [removeAttachment, { isLoading: isRemoving }] =
        useRemoveLeaveRequestAttachmentMutation();

    const canManage = canParentManageLeaveRequestAttachments(request);
    const attachments = request.attachments ?? [];
    const listFiles = useMemo(
        () => (request.attachments ?? []).map(toListFile),
        [request.attachments],
    );
    const remainingSlots = Math.max(MAX_ATTACHMENTS - attachments.length, 0);
    const isBusy = isCreating || isRemoving || busyId != null;

    useEffect(() => {
        onBusyChange?.(isBusy);
        return () => onBusyChange?.(false);
    }, [isBusy, onBusyChange]);

    async function handleAddLocalFiles(files: AttachmentFile[]) {
        if (!canManage || files.length === 0) {
            return;
        }

        for (const file of files) {
            const mimeType = resolveAttachmentMimeType(
                file.mimeType,
                file.name,
            );

            if (!mimeType) {
                toastError(
                    t("leave.errorTitle"),
                    t("leave.attachments.unsupportedType"),
                );
                continue;
            }

            if (file.size > MAX_ATTACHMENT_BYTES) {
                toastError(
                    t("leave.errorTitle"),
                    t("leave.attachments.tooLarge"),
                );
                continue;
            }

            try {
                setBusyId(file.id);
                const contentBase64 = await readAttachmentAsBase64(file.uri);
                const created = await createAttachment({
                    leave_request_id: request.id,
                    original_filename: file.name,
                    mime_type: mimeType,
                    content_base64: contentBase64,
                    skip_staff_email: true,
                }).unwrap();
                onAdded?.(created.id);
            } catch (error) {
                log.error("createLeaveRequestAttachment failed", error);
                toastError(
                    t("leave.errorTitle"),
                    getErrorMessage(error, t("leave.attachments.uploadFailed")),
                );
            } finally {
                setBusyId(null);
            }
        }
    }

    function handleRemove(id: string) {
        const attachment = attachments.find((item) => String(item.id) === id);

        if (!attachment || !canManage) {
            return;
        }

        showAlert(
            t("leave.attachments.removeConfirmTitle"),
            t("leave.attachments.removeConfirmMessage", {
                name: attachment.original_filename,
            }),
            [
                { text: t("leave.cancelConfirmKeep"), variant: "outline" },
                {
                    text: t("leave.attachments.remove"),
                    variant: "destructive",
                    testID: TEST_IDS[TEST_IDS.ATTACHMENT_REMOVE_CONFIRM_BUTTON],
                    onPress: async () => {
                        try {
                            setBusyId(id);
                            await removeAttachment(attachment.id).unwrap();
                            onRemoved?.(attachment.id);
                            toastSuccess(
                                t("leave.attachments.removedTitle"),
                                t("leave.attachments.removedBody"),
                            );
                        } catch (error) {
                            log.error(
                                "removeLeaveRequestAttachment failed",
                                error,
                            );
                            toastError(
                                t("leave.errorTitle"),
                                getErrorMessage(
                                    error,
                                    t("leave.attachments.removeFailed"),
                                ),
                            );
                        } finally {
                            setBusyId(null);
                        }
                    },
                },
            ],
        );
    }

    async function handleOpen(id: string) {
        const attachment = attachments.find((item) => String(item.id) === id);

        if (!attachment) {
            return;
        }

        try {
            setBusyId(id);
            await query.downloadLeaveRequestAttachment(attachment);
        } catch (error) {
            log.error("downloadLeaveRequestAttachment failed", error);
            toastError(
                t("leave.errorTitle"),
                getErrorMessage(error, t("leave.attachments.downloadFailed")),
            );
        } finally {
            setBusyId(null);
        }
    }

    if (!canManage && attachments.length === 0) {
        return null;
    }

    return (
        <View className="gap-3">
            <View className="gap-1">
                <Label>{t("leave.attachments.label")}</Label>
            </View>

            <AttachmentFileList
                files={listFiles}
                onOpen={handleOpen}
                onRemove={canManage ? handleRemove : undefined}
                busyId={busyId}
            />

            {canManage && remainingSlots > 0 ? (
                <AttachmentPicker
                    files={[]}
                    onChange={(files) => {
                        void handleAddLocalFiles(files);
                    }}
                    maxFiles={remainingSlots}
                    onPickerSessionChange={onPickerSessionChange}
                />
            ) : null}

            {isBusy ? (
                <Text variant="caption">{t("leave.attachments.working")}</Text>
            ) : null}
        </View>
    );
}
